"use client";

import { Input, Label, Checkbox, Textarea } from "@relume_io/relume-ui";
import { useState } from "react";
import { MonicateringTextLink } from "@/components/ui/MonicateringTextLink";
import {
  CONTACT_FORM_MESSAGE_PLACEHOLDER,
  CONTACT_FORM_SUBMIT_LABEL,
  CONTACT_FORM_SUCCESS_MESSAGE,
  isValidEmail,
  isValidPhone,
} from "@/lib/contact-form";
import { trackContactFormConversion } from "@/lib/track-contact-conversion";

type Contact2FormProps = {
  buttonTitle?: string;
};

const fieldClass =
  "!h-11 !rounded-none !border-0 !border-b !border-solid !border-on-primary/35 !bg-transparent !px-0 !py-2 !text-base !text-on-primary !shadow-none placeholder:!text-on-primary/45 focus-visible:!border-on-primary focus-visible:!ring-0 focus-visible:!ring-offset-0";

const textareaClass =
  "!min-h-[8rem] !rounded-none !border-0 !border-b !border-solid !border-on-primary/35 !bg-transparent !px-0 !py-2 !text-base !text-on-primary !shadow-none resize-y placeholder:!text-on-primary/45 focus-visible:!border-on-primary focus-visible:!ring-0 focus-visible:!ring-offset-0";

export function Contact2Form({ buttonTitle = CONTACT_FORM_SUBMIT_LABEL }: Contact2FormProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [acceptTerms, setAcceptTerms] = useState<boolean | "indeterminate">(false);
  const [website, setWebsite] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setMessage("");
    setAcceptTerms(false);
    setWebsite("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    const isTermsAccepted = acceptTerms === true;

    if (!trimmedName || !trimmedPhone) {
      setStatus({ type: "error", text: "Uzupełnij wszystkie wymagane pola." });
      return;
    }
    if (!isValidPhone(trimmedPhone)) {
      setStatus({ type: "error", text: "Podaj poprawny numer telefonu." });
      return;
    }
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setStatus({ type: "error", text: "Podaj poprawny adres e-mail." });
      return;
    }
    if (!isTermsAccepted) {
      setStatus({ type: "error", text: "Zaakceptuj politykę prywatności, aby wysłać formularz." });
      return;
    }

    setStatus(null);
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          phone: trimmedPhone,
          email: trimmedEmail,
          message: trimmedMessage,
          acceptTerms: isTermsAccepted,
          website,
        }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error || "Nie udało się wysłać formularza.");
      }

      resetForm();
      setStatus({ type: "success", text: CONTACT_FORM_SUCCESS_MESSAGE });
      trackContactFormConversion();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Błąd wysyłki formularza.";
      setStatus({ type: "error", text });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel = buttonTitle.trim() || CONTACT_FORM_SUBMIT_LABEL;

  return (
    <form
      data-no-reveal-scope
      className="grid grid-cols-1 grid-rows-[auto_auto] gap-6 text-on-primary"
      onSubmit={handleSubmit}
      noValidate
    >
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />

      <div className="grid w-full items-stretch gap-8">
        <div className="grid w-full">
          <Label htmlFor="contact2-name" className="sr-only">
            Imię
          </Label>
          <Input
            type="text"
            id="contact2-name"
            name="name"
            autoComplete="given-name"
            required
            placeholder="Imię *"
            aria-label="Imię"
            className={fieldClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          <div className="grid w-full">
            <Label htmlFor="contact2-phone" className="sr-only">
              Telefon
            </Label>
            <Input
              type="tel"
              id="contact2-phone"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              required
              placeholder="Telefon *"
              aria-label="Telefon"
              className={fieldClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="grid w-full">
            <Label htmlFor="contact2-email" className="sr-only">
              E-mail
            </Label>
            <Input
              type="email"
              id="contact2-email"
              name="email"
              autoComplete="email"
              placeholder="E-mail"
              aria-label="E-mail"
              className={fieldClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="grid w-full">
          <Label htmlFor="contact2-message" className="sr-only">
            Wiadomość
          </Label>
          <Textarea
            id="contact2-message"
            name="message"
            placeholder={CONTACT_FORM_MESSAGE_PLACEHOLDER}
            aria-label="Wiadomość"
            className={textareaClass}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col items-start gap-7 sm:mt-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6 md:mt-8">
        <div className="flex items-center gap-2 text-sm [&_button]:size-5 [&_button]:min-h-5 [&_button]:min-w-5 [&_button]:shrink-0 [&_button]:border-on-primary [&_button]:bg-transparent [&_svg]:text-on-primary">
          <Checkbox
            id="contact2-terms"
            checked={acceptTerms}
            onCheckedChange={setAcceptTerms}
            required
          />
          <Label htmlFor="contact2-terms" className="cursor-pointer leading-none">
            Akceptuję{" "}
            <a
              className="text-on-primary underline decoration-on-primary/80 underline-offset-2 hover:opacity-90"
              href="/polityka-prywatnosci"
              target="_blank"
              rel="noopener noreferrer"
            >
              politykę prywatności
            </a>
            . <span className="text-on-primary/70">*</span>
          </Label>
        </div>

        <MonicateringTextLink
          as="button"
          type="submit"
          tone="onDark"
          disabled={isSubmitting}
          className="shrink-0 self-end sm:ml-auto"
        >
          {isSubmitting ? "Wysyłanie..." : submitLabel}
        </MonicateringTextLink>
      </div>

      {status ? (
        <p
          role={status.type === "error" ? "alert" : "status"}
          className={
            status.type === "error" ? "text-center text-red-200" : "text-center text-green-200"
          }
        >
          {status.text}
        </p>
      ) : null}
    </form>
  );
}
